import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useWebSocket } from '../../context/WebSocketContext';
import { 
  getNotifications, 
  getUnreadNotificationCount, 
  markNotificationRead,
  markAllNotificationsRead
} from '../../lib/api';
import { 
  Bell, 
  AlertTriangle, 
  Wifi, 
  WifiOff, 
  Activity, 
  Check,
  CheckCheck,
  X,
  Zap,
  RefreshCw,
  ExternalLink
} from 'lucide-react';
import { Button } from '../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { ScrollArea } from '../ui/scroll-area';
import { cn, formatDate } from '../../lib/utils';
import { toast } from 'sonner';

const severityColors = {
  critical: 'bg-red-500/10 text-red-500 border-red-500/20',
  warning: 'bg-yellow-500/10 text-yellow-500 border-yellow-500/20',
  info: 'bg-blue-500/10 text-blue-500 border-blue-500/20',
};

const severityIcons = {
  critical: AlertTriangle,
  warning: Activity,
  info: Zap,
};

const alertTypeIcons = {
  charger_offline: WifiOff,
  charger_faulted: AlertTriangle,
  fault_cleared: Check,
  charger_flapping: RefreshCw,
  session_failed: X,
};

const NotificationBell = () => {
  const { token, user } = useAuth();
  const { subscribe } = useWebSocket();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);

  const fetchNotifications = useCallback(async () => {
    if (!token) return;
    try {
      setLoading(true);
      const [notificationsRes, countRes] = await Promise.all([
        getNotifications({ limit: 20 }),
        getUnreadNotificationCount()
      ]);
      setNotifications(notificationsRes.data || []);
      setUnreadCount(countRes.data?.count || 0);
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  // Clear unread count when dropdown opens
  useEffect(() => {
    if (open && unreadCount > 0) {
      // Mark all as read when opening
      handleMarkAllAsRead();
    }
  }, [open]);

  // Use centralized WebSocket for real-time notifications
  useEffect(() => {
    const unsubscribe = subscribe('notification', (message) => {
      const data = message.data || message;
      // Add new notification to the top
      setNotifications(prev => [data, ...prev].slice(0, 20));
      setUnreadCount(prev => prev + 1);
      
      // Show toast for critical notifications
      if (data.severity === 'critical') {
        toast.error(data.title, {
          description: data.message,
          duration: 10000,
        });
      } else if (data.severity === 'warning') {
        toast.warning(data.title, {
          description: data.message,
        });
      }
    });
    
    return () => unsubscribe();
  }, [subscribe]);

  const handleMarkAsRead = async (notificationId) => {
    try {
      await markNotificationRead(notificationId);
      setNotifications(prev => 
        prev.map(n => n.id === notificationId ? { ...n, is_read_by_me: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Failed to mark as read:', error);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await markAllNotificationsRead();
      setNotifications(prev => prev.map(n => ({ ...n, is_read_by_me: true })));
      setUnreadCount(0);
      toast.success('Të gjitha njoftimet u shënuan si të lexuara');
    } catch (error) {
      toast.error('Gabim në shënimin e njoftimeve');
    }
  };

  const getIcon = (alertType, severity) => {
    const Icon = alertTypeIcons[alertType] || severityIcons[severity] || Bell;
    return Icon;
  };

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button 
          variant="ghost" 
          size="icon" 
          className="relative"
          data-testid="notification-bell"
        >
          <Bell className="h-5 w-5" />
          {unreadCount > 0 && (
            <span className="absolute -top-1 -right-1 h-5 w-5 rounded-full bg-red-500 text-white text-xs flex items-center justify-center animate-pulse">
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent 
        align="end" 
        className="w-80 sm:w-96 p-0"
        data-testid="notification-dropdown"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-border">
          <div className="flex items-center gap-2">
            <Bell className="h-4 w-4" />
            <span className="font-medium">Njoftimet</span>
            {unreadCount > 0 && (
              <span className="text-xs bg-red-500/10 text-red-500 px-2 py-0.5 rounded-full">
                {unreadCount} të palexuara
              </span>
            )}
          </div>
          {unreadCount > 0 && (
            <Button
              variant="ghost"
              size="sm"
              className="text-xs"
              onClick={handleMarkAllAsRead}
            >
              <CheckCheck className="h-3 w-3 mr-1" />
              Shëno të gjitha
            </Button>
          )}
        </div>
        
        {/* Notifications List */}
        <ScrollArea className="h-[400px]">
          {loading ? (
            <div className="flex items-center justify-center h-32">
              <RefreshCw className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : notifications.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-32 text-muted-foreground">
              <Bell className="h-8 w-8 mb-2 opacity-50" />
              <p className="text-sm">Nuk ka njoftime</p>
            </div>
          ) : (
            <div className="divide-y divide-border">
              {notifications.map((notification) => {
                const Icon = getIcon(notification.alert_type, notification.severity);
                const isUnread = !notification.is_read_by_me;
                
                return (
                  <div
                    key={notification.id}
                    className={cn(
                      "p-4 hover:bg-muted/50 transition-colors cursor-pointer",
                      isUnread && "bg-primary/5"
                    )}
                    onClick={() => isUnread && handleMarkAsRead(notification.id)}
                    data-testid={`notification-${notification.id}`}
                  >
                    <div className="flex gap-3">
                      <div className={cn(
                        "w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 border",
                        severityColors[notification.severity] || severityColors.info
                      )}>
                        <Icon className="h-4 w-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className={cn(
                            "text-sm font-medium truncate",
                            isUnread && "text-foreground"
                          )}>
                            {notification.title}
                          </p>
                          {isUnread && (
                            <span className="w-2 h-2 rounded-full bg-primary flex-shrink-0" />
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">
                          {notification.message}
                        </p>
                        <div className="flex items-center gap-2 mt-1">
                          {notification.charger_name && (
                            <span className="text-xs text-muted-foreground bg-muted px-1.5 py-0.5 rounded">
                              {notification.charger_name}
                            </span>
                          )}
                          <span className="text-xs text-muted-foreground">
                            {formatDate(notification.created_at)}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </ScrollArea>
        
        {/* Footer */}
        <div className="p-2 border-t border-border">
          <Button 
            variant="ghost" 
            className="w-full text-sm"
            onClick={() => {
              setOpen(false);
              navigate('/notifications');
            }}
          >
            <ExternalLink className="h-4 w-4 mr-2" />
            Shiko të gjitha njoftimet
          </Button>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default NotificationBell;
